"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const STEPS = [
  {
    id: 1,
    title: "Project Information",
    description: "Enter your project details and basic information",
    fields: [
      { id: "title", label: "Project Title", placeholder: "Enter project name", type: "input" },
      { id: "description", label: "Brief Description", placeholder: "What is this project about?", type: "textarea" }
    ]
  },
  {
    id: 2,
    title: "Team Setup",
    description: "Configure your team members and roles",
    fields: [
      { id: "teamSize", label: "Team Size", placeholder: "Number of team members", type: "input" },
      { id: "lead", label: "Team Lead", placeholder: "Team lead name", type: "input" }
    ]
  },
  {
    id: 3,
    title: "Review & Confirm",
    description: "Review your information and confirm to proceed",
    fields: []
  }
];

export default function ModalStepperExample() {
  const [open, setOpen] = React.useState(true);
  const [step, setStep] = React.useState(1);
  const [formData, setFormData] = React.useState<Record<string, string>>({});

  const next = () => setStep(s => Math.min(3, s + 1));
  const back = () => setStep(s => Math.max(1, s - 1));
  const reset = () => { setStep(1); setFormData({}); };

  const updateField = (fieldId: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const currentStep = STEPS.find(s => s.id === step);
  const canProceed = step === 3 || (currentStep?.fields.every(field => formData[field.id]?.trim()) ?? false);

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Stepper Modal</Button>
      <Dialog open={open} onOpenChange={(v) => { if(!v) reset(); setOpen(v); }}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-[60vw] max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Project Setup Wizard</h2>
              <p className="mt-2 text-muted-foreground">Configure your project in a few simple steps</p>
              
              {/* Step Progress */}
              <div className="mt-4 flex items-center gap-2">
                {STEPS.map((s, index) => (
                  <React.Fragment key={s.id}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      s.id <= step 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-white/10 text-muted-foreground'
                    }`}>
                      {s.id < step ? '✓' : s.id}
                    </div>
                    {index < STEPS.length - 1 && (
                      <div className={`flex-1 h-1 rounded ${
                        s.id < step ? 'bg-primary' : 'bg-white/10'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              
              <div className="mt-3 flex items-center gap-4 text-sm">
                <Badge variant={step === 1 ? "default" : "secondary"}>Step {step} of 3</Badge>
                <Badge variant="outline">{currentStep?.title}</Badge>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto">
              <Card className="bg-white/5 border-white/20">
                <CardHeader>
                  <CardTitle className="text-xl">{currentStep?.title}</CardTitle>
                  <p className="text-muted-foreground">{currentStep?.description}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {step === 1 && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Project Title</label>
                        <Input 
                          placeholder="Enter project name" 
                          value={formData.title || ''}
                          onChange={(e) => updateField('title', e.target.value)}
                          className="bg-white/10 border-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Brief Description</label>
                        <Textarea 
                          placeholder="What is this project about?" 
                          value={formData.description || ''}
                          onChange={(e) => updateField('description', e.target.value)}
                          className="min-h-[120px] bg-white/10 border-white/20"
                        />
                      </div>
                    </>
                  )}
                  
                  {step === 2 && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Team Size</label>
                        <Input 
                          placeholder="Number of team members" 
                          value={formData.teamSize || ''}
                          onChange={(e) => updateField('teamSize', e.target.value)}
                          className="bg-white/10 border-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Team Lead</label>
                        <Input 
                          placeholder="Team lead name" 
                          value={formData.lead || ''}
                          onChange={(e) => updateField('lead', e.target.value)}
                          className="bg-white/10 border-white/20"
                        />
                      </div>
                    </>
                  )}
                  
                  {step === 3 && (
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                        <h4 className="font-medium text-foreground mb-3">Project Information</h4>
                        <div className="space-y-2 text-sm">
                          <div><span className="text-muted-foreground">Title:</span> {formData.title || 'Not provided'}</div>
                          <div><span className="text-muted-foreground">Description:</span> {formData.description || 'Not provided'}</div>
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-lg border border-white/10 bg-white/5">
                        <h4 className="font-medium text-foreground mb-3">Team Setup</h4>
                        <div className="space-y-2 text-sm">
                          <div><span className="text-muted-foreground">Team Size:</span> {formData.teamSize || 'Not provided'}</div>
                          <div><span className="text-muted-foreground">Team Lead:</span> {formData.lead || 'Not provided'}</div>
                        </div>
                      </div>
                      
                      <div className="text-center py-4">
                        <div className="text-green-600 font-medium">✓ Ready to complete setup</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Review your information above and click Finish to proceed
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {step === 3 ? (
                  <span className="text-green-600">All steps completed</span>
                ) : (
                  <span>Please complete the current step to continue</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={step === 1 ? () => setOpen(false) : back}>
                  {step === 1 ? 'Cancel' : 'Back'}
                </Button>
                {step < 3 ? (
                  <Button onClick={next} disabled={!canProceed}>Next</Button>
                ) : (
                  <Button onClick={() => setOpen(false)}>Complete Setup</Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
